import * as React from "react";
import * as moment from "moment";
import { CalendarEvents } from "source/app/presentation/MainPanel/Calendar/CalendarEvents/CalendarEvents";
import { GetSimplifiedEvents } from "source/app/services/External/LogicServer/Calendar/GetSimplifiedEvents";
import { ESharedComponent } from "source/app/services/UI/State/ESharedComponent";
import { State } from "source/app/services/UI/State/State";
import Nameof from "../../../services/NameOf";
import { Style } from "../../Shared/Styles/CSS";
import Data from "./../../../services/Data";
import { CalendarEvent } from "./CalendarEventUtils/CalendarEvent";
import { GetCalendarEventsCombinedWithFreeTimeEvents } from "./GetCalendarEventsCombinedWithFreeTimeEvents";
import { ICalendarState } from "./ICalendarState";
import { LoadingSpinner } from "./LoadingSpinner";
import { ShowWarningForExpiredEvents } from "./ShowWarningForExpiredEvents";
import IsNotEqual from "source/app/services/Shared/ReactHelpers";
import IconWithText from "source/app/services/Shared/IconWithText/IconWithText";
import { EGlyphicon } from "source/app/presentation/Shared/Glyphicon/EGlyphicon";
import { LaunchCalendarEventEditor } from "./CalendarEvents/Render/Editor/LaunchCalendarEventEditor";
import { GetNewCalendarEvent } from "./FreeHourNumbers/FreeTime/GetNewCalendarEvent";

class Calendar extends React.Component<{}, ICalendarState> {
    constructor(props: {}) {
        super(props);

        this.state = {
            eventData: [],
            hasErrorLoadingData: false,
            isLoading: true,
            hoursBeingChanged: [],
        };
    }

    async componentDidMount() {
        // Sharing class here for error handling
        State.ShareClass(ESharedComponent.Calendar, this);

        await Data.Load.Component({
            component: this,
            load: () => GetSimplifiedEvents(),
            stateTarget: Nameof<ICalendarState>("eventData"),
            inLoadAll: true,
        });

        this.setState({ isLoading: false });
        State.ShareClass(ESharedComponent.Calendar, this);
    }

    // Required to prevent unncessary renders
    // when Data.Load.All() is calle
    shouldComponentUpdate(nextProps: {}, nextState: ICalendarState) {
        return (
            IsNotEqual(nextProps, this.props) ||
            IsNotEqual(nextState, this.state)
        );
    }

    async componentDidUpdate() {
        State.ShareClass(ESharedComponent.Calendar, this);
    }

    componentWillUnmount() {
        Data.Unload(this);
    }

    render() {
        if (this.state.isLoading) {
            return <LoadingSpinner />;
        }

        if (this.state.hasErrorLoadingData) {
            return (
                <div style={{ textAlign: `center`, color: `coral` }}>
                    Error loading calendar data. Please check the console for
                    error details.
                </div>
            );
        }

        ShowWarningForExpiredEvents(this.state.eventData);

        const combinedCalendarEvents =
            GetCalendarEventsCombinedWithFreeTimeEvents(this.state.eventData);

        const filteredCalendarEvents = combinedCalendarEvents
            .map((calendarEvent) => new CalendarEvent(calendarEvent))
            .filter((calendarEvent) => calendarEvent.IsToday)
            .map((calendarEvent) => calendarEvent.eventData);

        return (
            <div
                id="CalendarContainer"
                {...Style(`calendarContainer`, {
                    maxWidth: `500px`,
                    margin: `auto`,
                })}
            >
                <AddCalendarEventLink />
                {filteredCalendarEvents.length === 0 && (
                    <div
                        style={{
                            textAlign: "center",
                            opacity: 0.5,
                            margin: "10px",
                        }}
                    >
                        No calendar data available
                    </div>
                )}
                <CalendarEvents
                    calendarEvents={filteredCalendarEvents}
                    state={this.state}
                />
            </div>
        );
    }
}

export default Calendar;

function AddCalendarEventLink() {
    return (
        <div
            style={{
                margin: "10px",
                textAlign: "center",
            }}
        >
            <IconWithText
                iconSuffix={EGlyphicon["plus-sign"]}
                whenClicked={() =>
                    LaunchCalendarEventEditor(
                        new CalendarEvent(GetNewEventStartingNow())
                    )
                }
                displayAsBlock={false}
            >
                Add
            </IconWithText>
        </div>
    );
}

function GetNewEventStartingNow() {
    const startTime = GetNearestTimeblockStart();
    const endTime = moment(startTime).add(1, "hour");

    return GetNewCalendarEvent(startTime.format(), endTime.format());
}

function GetNearestTimeblockStart() {
    const now = moment();
    const minutesUntilNextHour = 60 - now.minutes();
    const shouldSnapToNextHour = minutesUntilNextHour <= 15;

    if (shouldSnapToNextHour) {
        return now.add(1, "hour").startOf("hour");
    }

    return now.startOf("hour");
}
